import {
  CalendarIcon,
  ClipboardIcon,
  ClockIcon,
  ColorSwatchIcon,
  DocumentTextIcon,
  FlagIcon,
  MailIcon,
  MenuIcon,
  NewspaperIcon,
  OfficeBuildingIcon,
  PhoneIcon,
  PhotographIcon,
  PresentationChartLineIcon,
  TagIcon,
  TerminalIcon,
  TicketIcon,
  UserCircleIcon,
  UsersIcon,
} from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { buildTailwindPreviewHtml } from "components/htmlPreviewButton";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import { Link } from "react-router-dom";
import { CollapsibleSection } from "../../../components/collapsibleSection";
import { useOne, useParse } from "@refinedev/core";
import { IOrgDetail, SupportedLanguage } from "interfaces";
import { CONTENT_LANGUAGES, WEBSITE_HOST_NAME } from "utills/constants";
import { FullScreenLoading } from "components/fullScreenLoading";
import { Fragment, useState } from "react";
import { getPixFileName, isHaveActionPermission } from "utills/helpers";
import moment from "moment";
import { CaptivePortalIcon, CertificateIcon, CompanyIcon, TaxIcon } from "components/customIcons";
import Input from "components/input";
import { HistoryLogButton } from "components/historyLogButton";
import CONFIG from "app-config/config.json";
import { get } from "lodash";

export const OrganizationDetail: React.FC = () => {
  const parse = useParse();
  const { params } = parse();
  const [selectedLeftWebsiteFooterContentLanguage, setSelectedLeftWebsiteFooterContentLanguage] = useState(SupportedLanguage.EN);
  const [selectedRightWebsiteFooterContentLanguage, setSelectedRightWebsiteFooterContentLanguage] = useState(SupportedLanguage.EN);

  const { data, isFetching, isPreviousData, isLoading } = useOne<IOrgDetail>({
    resource: "organization",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const _generateWorkspaceDomain = (path: string) => {
    return `https://${path}.workspace.${WEBSITE_HOST_NAME}`;
  };

  const _generateLandingPageDomain = (path: string) => {
    return `https://${path}.${WEBSITE_HOST_NAME}`;
  };

  const _generateSlugDomain = (path: string) => {
    return `${CONFIG.DOMAIN}/organizers/${path}`;
  };

  const _generateFipixDomain = (path?: string) => {
    if (!path) return "";
    return path.startsWith("http://") || path.startsWith("https://")
      ? path
      : `https://${path}`;
  };

  const _renderColor = (color?: string) => (
    <div className="flex items-center gap-x-2">
      <div
        className="w-8 h-8 rounded-md border-1 border-solid border-black"
        style={{ backgroundColor: color ?? "" }}
      />
      <p className="font-medium">{color}</p>
    </div>
  );

  const organization = data?.data;

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Organization Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            <HistoryLogButton
              key="history-log"
              resource={`organization/${params?.id}/history`}
              className="h-11"
            />
            {isHaveActionPermission(
              "org_management",
              "organization",
              "edit"
            ) ? (
              <Link to={`/organization/org/${organization?.id}/edit`}>
                <Button>
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <CollapsibleSection label="Organization Information">
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={UsersIcon}
            label="Organization name"
            content={
              <div className="flex flex-col gap-y-3">
                {organization?.name_vi ? (
                  <div>
                    <Tag text="Vietnam" />
                    <p className="mt-1 text-sm leading-6">
                      {organization.name_vi}
                    </p>
                  </div>
                ) : (
                  <></>
                )}
                {organization?.name_en ? (
                  <div>
                    <Tag text="English" />
                    <p className="mt-1 text-sm leading-6">
                      {organization.name_en}
                    </p>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            }
          />
          <DescriptionItem
            icon={TagIcon}
            label="Status"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                <SwitchButton
                  disabled
                  checked={organization?.is_active ?? false}
                  onChange={() => {}}
                />
                {organization?.is_active ? "Active" : "Inactive"}
              </p>
            }
          />
          <DescriptionItem
            icon={TicketIcon}
            label="Event type"
            content={
              <p className="text-sm leading-6 capitalize">
                {organization?.event_type.join(", ")}
              </p>
            }
          />
          <DescriptionItem
            icon={FlagIcon}
            label="Country"
            content={
              <p className="text-sm leading-6">{organization?.country}</p>
            }
          />
          <DescriptionItem
            icon={PresentationChartLineIcon}
            label="Workspace domain"
            hint="The access link to the Organization's workspace tool."
            content={
              organization?.workspace_domain ? (
                <a
                  rel="noreferrer"
                  href={_generateWorkspaceDomain(organization.workspace_domain)}
                  className="text-brand-500 font-semibold hover:underline text-sm leading-6"
                  target="_blank"
                >
                  {_generateWorkspaceDomain(organization.workspace_domain)}
                </a>
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={TerminalIcon}
            label="Landing page domain"
            hint="The access link to the Organization's landing page."
            content={
              organization?.landing_page ? (
                <a
                  rel="noreferrer"
                  href={_generateLandingPageDomain(organization.landing_page)}
                  className="text-brand-500 font-semibold hover:underline text-sm leading-6"
                  target="_blank"
                >
                  {_generateLandingPageDomain(organization.landing_page)}
                </a>
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={TerminalIcon}
            label="ActiUp’s homepage position"
            content={
              <p className="text-sm leading-6">
                {organization?.homepage_position}
              </p>
            }
          />
          <DescriptionItem
            icon={OfficeBuildingIcon}
            label="Organization slug"
            hint="The access link to the Organization's details page on the ActiUp website."
            content={
              organization?.organization_slug ? (
                <a
                  rel="noreferrer"
                  href={_generateSlugDomain(organization.organization_slug)}
                  className="text-brand-500 font-semibold hover:underline text-sm leading-6"
                  target="_blank"
                >
                  {_generateSlugDomain(organization.organization_slug)}
                </a>
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={NewspaperIcon}
            label="Organization introduction"
            hint="The content will be displayed on the “About” tab of the Organization's details page on the ActiUp website."
            content={
              <div className="flex flex-col gap-y-3">
                {organization?.introduction_vi ? (
                  <div>
                    <Tag text="Vietnam" />
                    <div className="mt-1 reset-tw">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: `${organization?.introduction_vi}`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <></>
                )}
                {organization?.introduction_en ? (
                  <div>
                    <Tag text="English" />
                    <div className="mt-1 reset-tw">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: `${organization?.introduction_en}`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            }
          />
          <DescriptionItem
            icon={PhoneIcon}
            label="Contact phone"
            content={
              <p className="text-sm leading-6">
                {organization?.contact_phone 
                ? `${organization?.contact_phone_area_code 
                    ? `(+${organization?.contact_phone_area_code})` : ""} ${organization?.contact_phone}` 
                : ""}
              </p>
            }
          />
          <DescriptionItem
            icon={MailIcon}
            label="Contact email"
            content={
              <p className="text-sm leading-6">{organization?.contact_email}</p>
            }
          />
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Images and Color">
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={PhotographIcon}
            label="Cover banner (1400x300)"
            hint="The image will be displayed in the cover image position on the Organizer Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
            content={
              organization?.cover_banner ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.cover_banner}
                  alt="cover"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Organization logo (600x600)"
            hint="The image will be displayed in the profile picture position on the Organizer Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
            content={
              organization?.organization_logo ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.organization_logo}
                  alt="logo"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Rectangular promotion banner (950x300)"
            hint="The image will be used and displayed as the promotion banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
            content={
              organization?.rectangular_promotion_banner ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.rectangular_promotion_banner}
                  alt="Rectangular Promotion Banner"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Square promotion banner (300x300)"
            hint="The image will be used and displayed as the promotion banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
            content={
              organization?.square_promotion_banner ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.square_promotion_banner}
                  alt="Square Promotion Banner"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Organization banner (1440x600)"
            hint="The image will be displayed on the Organizer Information card of the Organizer List page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
            content={
              organization?.organization_banner ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.organization_banner}
                  alt="banner"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Brand logo"
            hint="The logo will be replaced for the ActiUp logo position when accessing the Organizer Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
            content={
              organization?.brand_logo ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.brand_logo}
                  alt="brand logo"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Brand color"
            hint="The selected color code will replace the header color of ActiUp when accessing the Organizer Detail page on the actiup.net website."
            content={
              <div className="flex items-center gap-x-2">
                <div
                  className="w-8 h-8 rounded-md border-1 border-solid border-black"
                  style={{ backgroundColor: organization?.brand_color ?? "" }}
                />
                <p className="font-medium">{organization?.brand_color}</p>
              </div>
            }
          />
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Seller identification"
        hint="Merchant's identification information based on Business Registration or Establishment License"
      >
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={CompanyIcon}
            label="Company Name"
            content={
              <p className="text-sm leading-6">{organization?.seller_company_name}</p>
            }
          />
          <DescriptionItem
            icon={TaxIcon}
            label="Company Tax ID"
            content={
              <p className="text-sm leading-6">{organization?.seller_company_tax}</p>
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Image or scanned copy of the business license"
            content={(organization?.seller_business_license ?? []).map(licenseUrl => (
              <Link to={licenseUrl} target="_blank" className="block w-fit text-sm text-brand-500 hover:underline font-medium [&:not(:first-child)]:mt-1">
                {getPixFileName(licenseUrl)}
              </Link>
            ))
            }
          />
          <DescriptionItem
            icon={CertificateIcon}
            label="Business/ Establishment certificate number"
            content={
              <p className="text-sm leading-6">{organization?.seller_business_certificate_number}</p>
            }
          />
          <DescriptionItem
            icon={CalendarIcon}
            label="Issue date"
            content={
              <p className="text-sm leading-6">
                {organization?.seller_business_issue_date ? moment(organization?.seller_business_issue_date).format("DD/MM/YYYY") : ""}
              </p>
            }
          />
          <DescriptionItem
            icon={CompanyIcon}
            label="Place of issue"
            content={
              <p className="text-sm leading-6">{organization?.seller_place_of_issue}</p>
            }
          />
          <DescriptionItem
            icon={CompanyIcon}
            label="Business address"
            content={
              <p className="text-sm leading-6">{organization?.seller_business_address}</p>
            }
          />
          <DescriptionItem
            icon={MailIcon}
            label="Email"
            content={
              <p className="text-sm leading-6">{organization?.seller_company_email}</p>
            }
          />
          <DescriptionItem
            icon={PhoneIcon}
            label="Phone"
            content={
              <p className="text-sm leading-6">
                {organization?.seller_company_phone 
                ? `${organization?.seller_company_phone_area_code 
                    ? `(+${organization?.seller_company_phone_area_code})` : ""} ${organization?.seller_company_phone}` 
                : ""}
              </p>
            }
          />
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="3rd party integration - Whitelabel"
        hideArrow={!organization?.use_whitelabel_integration}
        hideContent={!organization?.use_whitelabel_integration}
        customSubLabel={(
          <div className="mt-3">
            <SwitchButton 
              checked={organization?.use_whitelabel_integration ? true : false}
              onChange={() => {}}
              disabled={true}
              label="Use Whitelabel integration"
              hint="Used to integrate the ticket purchasing flow with partner websites"
            />
          </div>
        )}
      >
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={CaptivePortalIcon}
            label="Sub-domain integration"
            
            content={organization?.whitelabel_domain ? (
              <Link 
                to={organization?.whitelabel_domain}
                target="_blank"
                className="block w-fit text-sm text-brand-500 hover:underline font-medium [&:not(:first-child)]:mt-1"
              >
                {organization?.whitelabel_domain}
              </Link>
            ) : <></>}
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Favicon"
            content={
              organization?.whitelabel_favicon ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization?.whitelabel_favicon}
                  alt="Favicon"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={MailIcon}
            label="Email footer"
            content={organization?.whitelabel_email_footer ? (
              <p className="text-sm leading-6 whitespace-pre-wrap">{organization?.whitelabel_email_footer}</p>
            ) : <></>}
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Email footer text color"
            content={
              <div className="flex items-center gap-x-2">
                <div
                  className="w-8 h-8 rounded-md border-1 border-solid border-black"
                  style={{ backgroundColor: organization?.whitelabel_email_footer_text_color ?? "" }}
                />
                <p className="font-medium">{organization?.whitelabel_email_footer_text_color}</p>
              </div>
            }
          />
          <DescriptionItem
            icon={MailIcon}
            label="Email sender"
            content={
              <p className="text-sm leading-6">{organization?.whitelabel_sender_email}</p>
            }
          />
          {(organization?.whitelabel_menu ?? []).map((menu, index) => (
            <Fragment key={index}>
              <DescriptionItem
              icon={MenuIcon}
              label={`Menu ${index + 1}`}
              content={
                <div className="flex flex-col gap-y-3">
                  {menu.name_vi ? (
                    <div>
                      <Tag text="Vietnam" />
                      <p className="mt-1 text-sm leading-6">
                        {menu.name_vi}
                      </p>
                    </div>
                  ) : (
                    <></>
                  )}
                  {menu.name_en ? (
                    <div>
                      <Tag text="English" />
                      <p className="mt-1 text-sm leading-6">
                        {menu.name_en}
                      </p>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              }
            />
            <DescriptionItem
              icon={CaptivePortalIcon}
              label={`URL ${index + 1}`}
              content={menu.url ? (
                <Link 
                  to={menu.url}
                  target="_blank"
                  className="block w-fit text-sm text-brand-500 hover:underline font-medium [&:not(:first-child)]:mt-1"
                >
                  {menu.url}
                </Link>
              ) : <></>}
            />
            </Fragment>
          ))}
          <DescriptionItem
            icon={UserCircleIcon}
            label="Use Sign in/Signup - Whitelabel"
            content={<Input type="checkbox" checked={organization?.use_whitelabel_signin_signup} disabled />}
          />
          <DescriptionItem
            icon={ClipboardIcon}
            label="Use Orders - Whitelabel"
            content={<Input type="checkbox" checked={organization?.use_whitelabel_orders} disabled />}
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Footer color"
            content={
              <div className="flex items-center gap-x-2">
                <div
                  className="w-8 h-8 rounded-md border-1 border-solid border-black"
                  style={{ backgroundColor: organization?.whitelabel_footer_color ?? "" }}
                />
                <p className="font-medium">{organization?.whitelabel_footer_color}</p>
              </div>
            }
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Footer text color"
            content={
              <div className="flex items-center gap-x-2">
                <div
                  className="w-8 h-8 rounded-md border-1 border-solid border-black"
                  style={{ backgroundColor: organization?.whitelabel_footer_text_color ?? "" }}
                />
                <p className="font-medium">{organization?.whitelabel_footer_text_color}</p>
              </div>
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Left Website Footer content"
            additionalLabel={(
              <div className="flex flex-row ml-2 gap-x-1.5 p-1.5 w-fit rounded-full outline outline-solid outline-[1px] outline-brand-500">
                {CONTENT_LANGUAGES.map((lang, index) => (
                  <img
                    key={lang.value}
                    src={`/images/flags/circle-${lang.value}.png`}
                    alt="Language Icon"
                    className={`w-4 shrink-0 cursor-pointer ${lang.value !== selectedLeftWebsiteFooterContentLanguage ? 'opacity-20' : ''}`}
                    onClick={() => setSelectedLeftWebsiteFooterContentLanguage(lang.value)}
                  />
                ))}
              </div>
            )}
            content={
              <div>
                <div
                  className="mt-4 reset-tw"
                  dangerouslySetInnerHTML={{
                    __html: `${get(
                      organization,
                        `whitelabel_left_website_footer_content_${selectedLeftWebsiteFooterContentLanguage}`,
                      ""
                    ) || ""}`,
                  }}
                ></div>
              </div>
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Right Website Footer content"
            additionalLabel={(
              <div className="flex flex-row ml-2 gap-x-1.5 p-1.5 w-fit rounded-full outline outline-solid outline-[1px] outline-brand-500">
                {CONTENT_LANGUAGES.map((lang, index) => (
                  <img
                    key={lang.value}
                    src={`/images/flags/circle-${lang.value}.png`}
                    alt="Language Icon"
                    className={`w-4 shrink-0 cursor-pointer ${lang.value !== selectedRightWebsiteFooterContentLanguage ? 'opacity-20' : ''}`}
                    onClick={() => setSelectedRightWebsiteFooterContentLanguage(lang.value)}
                  />
                ))}
              </div>
            )}
            content={
              <div>
                <div
                  className="mt-4 reset-tw"
                  dangerouslySetInnerHTML={{
                    __html: `${get(
                      organization,
                        `whitelabel_right_website_footer_content_${selectedRightWebsiteFooterContentLanguage}`,
                      ""
                    ) || ""}`,
                  }}
                ></div>
              </div>
            }
          />
        </div>
      </CollapsibleSection>
      <CollapsibleSection
        label="3rd party integration - Fipix Whitelabel"
        hideArrow={!organization?.use_fipix_integration}
        hideContent={!organization?.use_fipix_integration}
        customSubLabel={(
          <div className="mt-3">
            <SwitchButton
              checked={organization?.use_fipix_integration ? true : false}
              onChange={() => {}}
              disabled={true}
              label="Use Fipix integration"
              hint="Used to configure the Fipix whitelabel photo flow"
            />
          </div>
        )}
      >
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={CaptivePortalIcon}
            label="Fipix domain name"
            content={organization?.fipix_domain ? (
              <Link
                to={_generateFipixDomain(organization.fipix_domain)}
                target="_blank"
                className="block w-fit text-sm text-brand-500 hover:underline font-medium [&:not(:first-child)]:mt-1"
              >
                {organization.fipix_domain}
              </Link>
            ) : <></>}
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Brand logo URL"
            content={
              organization?.fipix_brand_logo_url ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.fipix_brand_logo_url}
                  alt="Fipix brand logo"
                />
              ) : (
                <></>
              )
            }
          />
          {(organization?.fipix_menu ?? []).map((menu, index) => (
            <Fragment key={index}>
              <DescriptionItem
                icon={MenuIcon}
                label={`Menu ${index + 1}`}
                content={
                  <div className="flex flex-col gap-y-3">
                    {menu.name_vi ? (
                      <div>
                        <Tag text="Vietnam" />
                        <p className="mt-1 text-sm leading-6">
                          {menu.name_vi}
                        </p>
                      </div>
                    ) : (
                      <></>
                    )}
                    {menu.name_en ? (
                      <div>
                        <Tag text="English" />
                        <p className="mt-1 text-sm leading-6">
                          {menu.name_en}
                        </p>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                }
              />
              <DescriptionItem
                icon={CaptivePortalIcon}
                label={`URL ${index + 1}`}
                content={menu.url ? (
                  <Link
                    to={menu.url}
                    target="_blank"
                    className="block w-fit text-sm text-brand-500 hover:underline font-medium [&:not(:first-child)]:mt-1"
                  >
                    {menu.url}
                  </Link>
                ) : <></>}
              />
            </Fragment>
          ))}
          <DescriptionItem
            icon={DocumentTextIcon}
            label="SEO title"
            content={
              <p className="text-sm leading-6">{organization?.fipix_seo_title}</p>
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="SEO description"
            content={
              <p className="text-sm leading-6">
                {organization?.fipix_seo_description}
              </p>
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="SEO OG image"
            content={
              organization?.fipix_seo_og_image ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.fipix_seo_og_image}
                  alt="Fipix SEO OG"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Favicon URL"
            content={
              organization?.fipix_favicon ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={organization.fipix_favicon}
                  alt="Fipix favicon"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Background color"
            content={_renderColor(organization?.fipix_background_color)}
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Second background color"
            content={_renderColor(organization?.fipix_second_background_color)}
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Main text color"
            content={_renderColor(organization?.fipix_main_text_color)}
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Header background color"
            content={_renderColor(organization?.fipix_header_background_color)}
          />
          <DescriptionItem
            icon={ColorSwatchIcon}
            label="Menu text color"
            content={_renderColor(organization?.fipix_menu_text_color)}
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Footer HTML (Vietnamese)"
            content={
              organization?.fipix_footer_html_vi ? (
                <iframe
                  title="Footer HTML (Vietnamese)"
                  srcDoc={buildTailwindPreviewHtml(
                    organization.fipix_footer_html_vi
                  )}
                  sandbox="allow-scripts"
                  className="h-[360px] w-full rounded-md border border-solid border-gray-200 bg-white"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Footer HTML (English)"
            content={
              organization?.fipix_footer_html_en ? (
                <iframe
                  title="Footer HTML (English)"
                  srcDoc={buildTailwindPreviewHtml(
                    organization.fipix_footer_html_en
                  )}
                  sandbox="allow-scripts"
                  className="h-[360px] w-full rounded-md border border-solid border-gray-200 bg-white"
                />
              ) : (
                <></>
              )
            }
          />
        </div>
      </CollapsibleSection>
      {isFetching && isPreviousData ? <FullScreenLoading /> : <></>}
    </div>
  );
};
